import type { BeePluginOnCommentPayload } from '@beefree.io/sdk/dist/types/bee';
import { OnCommentChangeEnum } from '@beefree.io/sdk/dist/types/bee';
import { observable } from 'mobx';
import type { JSX, ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import type { ITemplate, Template } from '@feathr/blackbox';
import { TemplateClass } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import type {
  Bee,
  BeePluginError,
  IBeeEditorAction,
  IBeeEditorActions,
  IBeeEditorConfig,
  IPluginForm,
  TResolveHandler,
} from '@feathr/extender/components/BeeEditor';
import {
  EBeeEditorAction,
  formCustomFieldsFilters,
  getMergefieldCustomFieldsFilters,
} from '@feathr/extender/components/BeeEditor';
import { useAccount, useStore, useUser } from '@feathr/extender/state';

export enum TemplateEditorState {
  ready = 'ready',
  saving = 'saving',
  sending = 'sending',
  previewing = 'previewing',
  addingDynamicContent = 'addingDynamicContent',
  configuringFeathrForm = 'configuringFeathrForm',
  configuringLegacyForm = 'configuringLegacyForm',
  resolvingSave = 'resolvingSave',
  resolvingSend = 'resolvingSend',
  resolvingPreview = 'resolvingPreview',
  showingPreview = 'showingPreview',
  showingSend = 'showingSend',
  error = 'error',
}

interface ITemplateEditorState {
  state: TemplateEditorState;
  json?: string;
  error?: BeePluginError;
  form?: IPluginForm['structure'];
  customFields?: Record<string, unknown>;
  resolve?: TResolveHandler;
  reject?: () => Promise<void>;
}

function reducer(state: ITemplateEditorState, action: IBeeEditorAction): ITemplateEditorState {
  const resolveMap: Partial<Record<TemplateEditorState, TemplateEditorState>> = {
    [TemplateEditorState.error]: TemplateEditorState.ready,
    [TemplateEditorState.saving]: TemplateEditorState.resolvingSave,
    [TemplateEditorState.sending]: TemplateEditorState.resolvingSend,
    [TemplateEditorState.previewing]: TemplateEditorState.resolvingPreview,
    [TemplateEditorState.resolvingSave]: TemplateEditorState.ready,
    [TemplateEditorState.resolvingSend]: TemplateEditorState.showingSend,
    [TemplateEditorState.resolvingPreview]: TemplateEditorState.showingPreview,
    [TemplateEditorState.showingSend]: TemplateEditorState.ready,
    [TemplateEditorState.showingPreview]: TemplateEditorState.ready,
    [TemplateEditorState.addingDynamicContent]: TemplateEditorState.ready,
    [TemplateEditorState.configuringFeathrForm]: TemplateEditorState.ready,
    [TemplateEditorState.configuringLegacyForm]: TemplateEditorState.ready,
  };

  switch (action.type) {
    case EBeeEditorAction.save:
      return { ...state, state: TemplateEditorState.saving };

    case EBeeEditorAction.send:
      return { ...state, state: TemplateEditorState.sending };

    case EBeeEditorAction.preview:
      return { ...state, state: TemplateEditorState.previewing };

    case EBeeEditorAction.error:
      return { ...state, error: action.error, state: TemplateEditorState.error };

    case EBeeEditorAction.addDynamicContent:
      return {
        ...state,
        state: TemplateEditorState.addingDynamicContent,
        resolve: action.resolve,
        reject: action.reject,
      };

    case EBeeEditorAction.configureFeathrForm:
      return {
        ...state,
        customFields: { ...state.customFields, ...action.customFields },
        state: TemplateEditorState.configuringFeathrForm,
        resolve: action.resolve,
        reject: action.reject,
      };

    case EBeeEditorAction.configureLegacyForm:
      return {
        ...state,
        form: observable(action.form!),
        state: TemplateEditorState.configuringLegacyForm,
        resolve: action.resolve,
        reject: action.reject,
      };

    case EBeeEditorAction.resolve:
      return {
        ...state,
        state: resolveMap[state.state] as TemplateEditorState,
      };

    default:
      return state;
  }
}

interface IOptions {
  setBeePlugin?: (plugin: Bee) => void;
  isPinpoint?: boolean;
  isPartnerMessage?: boolean;
}

interface IBeeEditorProviderProps {
  readonly children: ReactNode;
  options?: IOptions;
  template: Template;
}

interface IBeeEditorContext {
  config: Omit<IBeeEditorConfig, 'mergefields'>;
  editorState: ITemplateEditorState;
  dispatchEditorAction: (action: IBeeEditorAction) => void;
}

const BeeEditorContext = createContext<IBeeEditorContext | undefined>(undefined);

/*
 * See Kent C. Dodds' article on effective context usage:
 * https://kentcdodds.com/blog/how-to-use-react-context-effectively
 */
function BeeEditorProvider({
  children,
  options = {},
  template,
}: Readonly<IBeeEditorProviderProps>): JSX.Element {
  const { CustomFields, Fonts, UserNotifications } = useStore();
  const account = useAccount();
  const user = useUser();
  const { t } = useTranslation();

  const initialState: ITemplateEditorState = {
    customFields: {
      eventId: template.get('event'),
      templateType: template.get('_cls'),
    },
    error: undefined,
    json: '',
    state: TemplateEditorState.ready,
  };
  const [editorState, dispatchEditorAction] = useReducer(reducer, initialState);

  const memoizedValue = useMemo(() => {
    const { setBeePlugin, isPinpoint = false, isPartnerMessage = false } = options;

    const handleConfigureFeathrForm: IBeeEditorActions['handleConfigureFeathrForm'] = async (
      resolve,
      reject,
      args,
    ) => {
      return dispatchEditorAction({
        resolve,
        reject,
        type: EBeeEditorAction.configureFeathrForm,
        customFields: { ...editorState.customFields, ...args.value.customFields },
      });
    };

    const handleConfigureLegacyForm: IBeeEditorActions['handleConfigureLegacyForm'] = async (
      resolve,
      reject,
      args,
    ) => {
      return dispatchEditorAction({
        form: args,
        resolve,
        reject,
        type: EBeeEditorAction.configureLegacyForm,
      });
    };

    function onError(error: BeePluginError): void {
      dispatchEditorAction({ type: EBeeEditorAction.error, error });
    }

    function onSend(): void {
      dispatchEditorAction({ type: EBeeEditorAction.resolve, json: undefined });
    }

    async function handleSave(): Promise<void> {
      const response = await template.patchDirty();

      if (response.isErrored) {
        toast(t("Couldn't save template. Try again."), { type: 'error' });
        return;
      }

      toast(t('Saved template.'), { type: 'success' });
    }

    function onAutoSave(json: string): void {
      // Only auto-save if the template is not already being saved
      if (editorState.state === TemplateEditorState.saving) {
        return;
      }

      // Only auto-save if the template is dirty
      if (!template.isDirty) {
        return;
      }

      async function saveAndDispatch(): Promise<void> {
        dispatchEditorAction({ type: EBeeEditorAction.save, json });
        await handleSave();
        dispatchEditorAction({ type: EBeeEditorAction.resolve });
      }

      saveAndDispatch();
    }

    function onSave(json: string): void {
      dispatchEditorAction({ type: EBeeEditorAction.resolve, json });
    }

    function onChange(json: string): void {
      const templateContent: ITemplate['content'] = template.get('content');
      template.set({ content: { ...templateContent, json } });
    }

    // commentPayload will be used when implementing notifications in https://github.com/Feathr/shrike/issues/4238.
    function onComment(
      { change: { type, payload } }: BeePluginOnCommentPayload,
      json: string,
    ): void {
      onChange(json);
      if (type === OnCommentChangeEnum.NEWCOMMENT && 'comment' in payload) {
        const {
          comment: { mentions },
          commentId,
        } = payload;

        if (mentions.length > 0) {
          template.notifyMentionedUsers(mentions, commentId);
        }
      }
      /**
       * It's imperative to save after commenting to ensure the element being commented on
       * accurately represents the state the user wants others to see.
       */
      template.patchDirty();
    }

    const fonts = Fonts.list({ only: ['id', 'name'], ordering: ['id'] });

    const cls = template.get('_cls');
    const mergefieldCustomFields = CustomFields.list({
      filters: getMergefieldCustomFieldsFilters(template.get('_cls')),
      pagination: { page: 0, page_size: 1000 },
    });
    const formCustomFields = [
      TemplateClass.Page,
      TemplateClass.LandingPage,
      TemplateClass.ReferralPage,
    ].includes(cls)
      ? CustomFields.list({
          filters: formCustomFieldsFilters,
          pagination: { page: 0, page_size: 1000 },
        })
      : CustomFields.newListResponse();

    const config: Omit<IBeeEditorConfig, 'mergefields'> = {
      actions: {
        handleConfigureFeathrForm,
        handleConfigureLegacyForm,
        onComment,
        onChange,
        onError,
        onAutoSave,
        onSave,
        onSend,
        dispatchEditorAction,
        setBeePlugin,
      },
      models: {
        account,
        fonts,
        formCustomFields,
        mergefieldCustomFields,
        user,
        UserNotifications,
      },
      isPinpoint,
      isPartnerMessage,
      t,
      template,
    };
    return {
      config,
      editorState,
      dispatchEditorAction,
    };
  }, [account, CustomFields, dispatchEditorAction, editorState, Fonts, options, t, template]);

  return <BeeEditorContext.Provider value={memoizedValue}>{children}</BeeEditorContext.Provider>;
}

function useBeeEditor(): IBeeEditorContext {
  const context = useContext(BeeEditorContext);

  if (context === undefined) {
    throw new Error('useBeeEditor hook must be wrapped in an BeeEditorContext context provider');
  }
  return context;
}

export { BeeEditorProvider, useBeeEditor };
